import { createElement } from 'react';
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import Axios, { isAxiosError } from 'axios';
import getCookie from '../../utils/getCookie';
import handleAPIPermissionError from '../../utils/handleAPIPermissionError';
import { enqueueSnackbar } from 'notistack';
import queryString from 'query-string';
import datadogActions, { DATADOG_EVENT } from 'lib/datadog';
import { AccessDeniedAlert } from 'components/common';
import theme from '../../theme';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export const apiClient = Axios.create({
  withCredentials: true,
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'X-CSRFToken': getCookie('csrftoken'),
  },
  paramsSerializer: (params) => queryString.stringify(params, { arrayFormat: 'comma' }),
});

type ApiRequestWithMeta = InternalAxiosRequestConfig & {
  metadata?: {
    startTime: number;
  };
};

apiClient.interceptors.request.use((config) => {
  const nextConfig = config as ApiRequestWithMeta;
  nextConfig.metadata = { startTime: performance.now() };
  return nextConfig;
});

apiClient.interceptors.response.use(
  (response) => {
    const requestConfig = response.config as ApiRequestWithMeta;
    const durationMs = requestConfig.metadata?.startTime
      ? Math.round(performance.now() - requestConfig.metadata.startTime)
      : undefined;

    datadogActions.trackDatadogAction(DATADOG_EVENT.apiCall, {
      method: requestConfig.method?.toUpperCase(),
      url: requestConfig.url,
      status: response.status,
      durationMs,
      isError: false,
    });

    return response;
  },
  (error: AxiosError) => {
    const requestConfig = error.config as ApiRequestWithMeta | undefined;
    const durationMs = requestConfig?.metadata?.startTime
      ? Math.round(performance.now() - requestConfig.metadata.startTime)
      : undefined;

    const responseData = error.response?.data as { detail?: string; message?: string } | undefined;
    const errorMessage = responseData?.detail ?? responseData?.message ?? error.message;

    datadogActions.trackDatadogAction(DATADOG_EVENT.apiCall, {
      method: requestConfig?.method?.toUpperCase(),
      url: requestConfig?.url,
      status: error.response?.status,
      durationMs,
      isError: true,
    });

    datadogActions.trackDatadogError(error, {
      source: 'api_client',
      url: requestConfig?.url,
      method: requestConfig?.method?.toUpperCase(),
      status: error.response?.status,
      errorType: error.name,
      errorCode: error.code,
      errorMessage,
    });

    if (
      isAxiosError(error) &&
      error?.response &&
      error?.response?.status === 403
    ) {
      enqueueSnackbar(
        createElement(AccessDeniedAlert, {
          message:
            (error?.response?.data as { detail?: string })?.detail ??
            'Access denied',
        }),
        {
          autoHideDuration: 10000,
          anchorOrigin: {
            vertical: 'top',
            horizontal: 'center',
          },
          style: {
            paddingBottom: 0,
            paddingTop: 0,
            backgroundColor: 'rgba(203, 46, 61, 1)',
            color: 'white',
            borderRadius: theme.shape.borderRadius,
            boxShadow: 'none',
            marginLeft: 0,
          },
        }
      );
      return error;
    }
    if (
      isAxiosError(error) &&
      error?.response &&
      error?.response?.status >= 500
    ) {
      handleAPIPermissionError(error);
    }
    return error;
  }
);
